import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, Divider, MenuItem, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { providersApi } from '../services/api';

/**
 * Mirrors the rungs of ProviderEligibility.isListable so an admin sees exactly what is keeping
 * a profile out of public search — a freshly imported one is hidden AND consent-pending, and
 * "it just doesn't appear" is otherwise impossible to diagnose from this screen.
 */
const notListableReasons = (provider) => {
  const reasons = [];
  if (provider.providerState !== 'APPROVED') reasons.push(`state is ${provider.providerState}`);
  if (provider.visible === false) reasons.push('profile is hidden');
  if (provider.adminSuspended) reasons.push('admin-suspended');
  if (!provider.claimed && provider.consentStatus !== 'GRANTED') {
    reasons.push(`consent is ${provider.consentStatus || 'PENDING'}`);
  }
  return reasons.length > 0 ? reasons : ['reason unknown'];
};

/**
 * Admin-only seeded-provider tools: claim/consent state chips, the append-only consent
 * event ledger (corrections are NEW events — nothing is edited), and send-claim (spec: flow B).
 *
 * Send-claim is not rate-limited here: the 6h cooldown guards the PUBLIC self-service endpoint
 * against mail-bombing a business, which does not apply to an authenticated admin whose sends
 * are all logged server-side.
 */
const SeededProviderPanel = ({ provider, showSuccess, showError }) => {
  const [consentLog, setConsentLog] = useState(null);
  const [eventForm, setEventForm] = useState({ event: 'GRANTED', sourceChannel: 'INSTAGRAM', grantedBy: '', note: '' });
  const [sending, setSending] = useState(false);
  const [grantingConsent, setGrantingConsent] = useState(false);
  // Held in component state only — the link is a bearer credential and must not be persisted
  // anywhere (no localStorage), so it disappears on navigation or reload.
  const [manualLink, setManualLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);

  const loadConsent = useCallback(() => {
    providersApi.getConsentEvents(provider.id).then(setConsentLog).catch(() => setConsentLog(null));
  }, [provider.id]);

  useEffect(() => {
    if (provider?.seeded) loadConsent();
  }, [provider, loadConsent]);

  if (!provider?.seeded) return null;

  const handleRecordEvent = async () => {
    try {
      await providersApi.recordConsentEvent(provider.id, eventForm);
      showSuccess('Consent event recorded');
      setEventForm({ event: 'GRANTED', sourceChannel: 'INSTAGRAM', grantedBy: '', note: '' });
      loadConsent();
    } catch (e) {
      showError(`Failed to record consent event: ${e.message}`);
    }
  };

  const handleApproveConsent = async () => {
    setGrantingConsent(true);
    try {
      await providersApi.recordConsentEvent(provider.id, {
        event: 'GRANTED',
        sourceChannel: eventForm.sourceChannel,
        grantedBy: eventForm.grantedBy || null,
        note: 'Approved from the admin panel',
      });
      showSuccess('Consent approved');
      loadConsent();
    } catch (e) {
      showError(`Failed to approve consent: ${e.message}`);
    } finally {
      setGrantingConsent(false);
    }
  };

  const handleGenerateLink = async () => {
    setGeneratingLink(true);
    try {
      setManualLink(await providersApi.generateSeededClaimLink(provider.id));
      showSuccess('Claim link generated — earlier links no longer work');
    } catch (e) {
      showError(`Failed to generate claim link: ${e.message}`);
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(manualLink.url);
      showSuccess('Link copied');
    } catch (e) {
      // Clipboard needs a secure context; the field is selectable so manual copy still works.
      showError('Could not copy automatically — select the link and copy it manually');
    }
  };

  const handleSendClaim = async () => {
    setSending(true);
    try {
      await providersApi.sendSeededClaim(provider.id);
      showSuccess('Claim email queued');
    } catch (e) {
      showError(`Failed to send claim email: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
          <Typography variant="h6">Seeded profile</Typography>
          <Chip size="small" color="secondary" label="SEEDED" />
          <Chip
            size="small"
            color={provider.claimed ? 'success' : 'warning'}
            label={provider.claimed ? 'CLAIMED' : 'UNCLAIMED'}
          />
          {provider.claimed && (
            <Chip
              size="small"
              color={provider.ownerConfirmed ? 'success' : 'default'}
              label={provider.ownerConfirmed ? 'OWNER CONFIRMED' : 'AWAITING OWNER CONFIRMATION'}
            />
          )}
          {consentLog && <Chip size="small" variant="outlined" label={`Consent: ${consentLog.currentStatus}`} />}
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {provider.businessCity || 'No city recorded'}
        </Typography>
        {/* Outreach channels for this profile. An unclaimed seeded provider has no linked
            user, so these provider-level fields are the only contact details that exist. */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {[provider.publicEmail, provider.publicPhone, provider.instagramUser, provider.publicWebsite]
            .filter(Boolean)
            .join(' · ') || 'No contact details recorded'}
        </Typography>

        {provider.listable === false && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Not publicly listed yet — {notListableReasons(provider).join('; ')}.
          </Alert>
        )}

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
          {/* One-click GRANTED. Still an append-only ledger event via the same single writer as
              the form below — this only pre-fills the common case, it is not a second path. */}
          {consentLog && consentLog.currentStatus !== 'GRANTED' && (
            <Button
              variant="contained"
              size="small"
              color="success"
              startIcon={<CheckCircleIcon />}
              disabled={grantingConsent}
              onClick={handleApproveConsent}
            >
              {grantingConsent ? 'Approving…' : 'Approve consent'}
            </Button>
          )}
        </Stack>

        {!provider.claimed && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
            {/* No cooldown gate: an admin press always sends. Each send mints a fresh token and
                supersedes the previous one, so the business's newest link is the working one. */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<SendIcon />}
              disabled={sending}
              onClick={handleSendClaim}
            >
              {sending ? 'Sending…' : 'Send claim link'}
            </Button>
            {/* For businesses reachable on social but not by email. */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<LinkIcon />}
              disabled={generatingLink}
              onClick={handleGenerateLink}
            >
              {generatingLink ? 'Generating…' : 'Generate link to send manually'}
            </Button>
          </Stack>
        )}

        {manualLink && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setManualLink(null)}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Anyone with this link can claim the profile — send it only to the business owner.
              It replaces any earlier link, expires{' '}
              {new Date(manualLink.expiresAt).toLocaleDateString()}, and is shown once: close this
              or reload and it is gone.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                fullWidth
                value={manualLink.url}
                InputProps={{ readOnly: true }}
                onFocus={(e) => e.target.select()}
              />
              <Button size="small" variant="contained" startIcon={<ContentCopyIcon />} onClick={handleCopyLink}>
                Copy
              </Button>
            </Stack>
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Consent ledger (append-only)
        </Typography>
        {consentLog?.events?.length ? (
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Event</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Granted by</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Recorded by</TableCell>
                <TableCell>At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {consentLog.events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell>{event.event}</TableCell>
                  <TableCell>{event.sourceChannel || '—'}</TableCell>
                  <TableCell>{event.grantedBy || '—'}</TableCell>
                  <TableCell>{event.note || '—'}</TableCell>
                  <TableCell>{event.recordedBy}</TableCell>
                  <TableCell>{new Date(event.recordedAt).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No consent events recorded.
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            select size="small" label="Event" value={eventForm.event}
            onChange={(e) => setEventForm({ ...eventForm, event: e.target.value })}
            sx={{ minWidth: 140 }}
          >
            {['GRANTED', 'DECLINED', 'WITHDRAWN'].map((v) => (
              <MenuItem key={v} value={v}>{v}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" label="Channel" value={eventForm.sourceChannel}
            onChange={(e) => setEventForm({ ...eventForm, sourceChannel: e.target.value })}
            sx={{ minWidth: 140 }}
          >
            {['INSTAGRAM', 'EMAIL', 'PHONE', 'OTHER'].map((v) => (
              <MenuItem key={v} value={v}>{v}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small" label="Granted by" value={eventForm.grantedBy}
            onChange={(e) => setEventForm({ ...eventForm, grantedBy: e.target.value })}
          />
          <TextField
            size="small" label="Note" value={eventForm.note}
            onChange={(e) => setEventForm({ ...eventForm, note: e.target.value })}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <Button variant="contained" size="small" onClick={handleRecordEvent}>
            Record event
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SeededProviderPanel;
